import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Autocomplete,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Drawer,
  Divider,
} from '@mui/material';
import api from '../../utils/api';
import JobSupportSeekerWorkflowDrawer from './JobSupportSeekerWorkflowDrawer';

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return name ? `${name} (${c.email || c.contact_type || ''})` : c.email || c.id;
}

function formatRateRange(min, max, suffix = '') {
  if (min == null && max == null) return '—';
  if (min != null && max != null) return `$${min}–$${max}${suffix}`;
  if (min != null) return `from $${min}${suffix}`;
  return `up to $${max}${suffix}`;
}

function supportingSummary(seekers) {
  const list = Array.isArray(seekers) ? seekers : [];
  if (!list.length) return '—';
  return list
    .map((s) => {
      const name = contactLabel(s.contact) || s.request_number;
      return name;
    })
    .join('; ');
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const emptyRateForm = {
  provider_kind: 'individual',
  skills: '',
  notes: '',
  status: 'active',
  rate_hourly_min: '',
  rate_hourly_max: '',
  rate_monthly_min: '',
  rate_monthly_max: '',
  rate_hours_per_day: '',
  rate_days_per_week: '',
  overtime_hourly_rate: '',
  rate_currency: 'USD',
  rate_notes: '',
};

function RateFields({ form, onChange }) {
  const patch = (k, v) => onChange({ ...form, [k]: v });
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
        Rate expectations (USD)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Hourly range, and/or monthly range with hours/day and days/week included. Extra hours beyond the monthly
        allowance are charged at the overtime hourly rate.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Hourly min ($)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_hourly_min}
            onChange={(e) => patch('rate_hourly_min', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Hourly max ($)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_hourly_max}
            onChange={(e) => patch('rate_hourly_max', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Monthly min ($)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_monthly_min}
            onChange={(e) => patch('rate_monthly_min', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Monthly max ($)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_monthly_max}
            onChange={(e) => patch('rate_monthly_max', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Hours/day (in monthly rate)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_hours_per_day}
            onChange={(e) => patch('rate_hours_per_day', e.target.value)}
            helperText="Included under monthly rate"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Days/week (in monthly rate)"
            type="number"
            fullWidth
            margin="dense"
            value={form.rate_days_per_week}
            onChange={(e) => patch('rate_days_per_week', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Extra / overtime ($/hr)"
            type="number"
            fullWidth
            margin="dense"
            value={form.overtime_hourly_rate}
            onChange={(e) => patch('overtime_hourly_rate', e.target.value)}
            helperText="Beyond monthly allowance"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Rate notes"
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            value={form.rate_notes}
            onChange={(e) => patch('rate_notes', e.target.value)}
          />
        </Grid>
      </Grid>
    </>
  );
}

function toPayloadRates(form) {
  return {
    rate_hourly_min: form.rate_hourly_min || null,
    rate_hourly_max: form.rate_hourly_max || null,
    rate_monthly_min: form.rate_monthly_min || null,
    rate_monthly_max: form.rate_monthly_max || null,
    rate_hours_per_day: form.rate_hours_per_day || null,
    rate_days_per_week: form.rate_days_per_week || null,
    overtime_hourly_rate: form.overtime_hourly_rate || null,
    rate_currency: form.rate_currency || 'USD',
    rate_notes: form.rate_notes || null,
  };
}

function formFromDetail(d) {
  return {
    provider_kind: d.provider_kind || 'individual',
    skills: d.skills || '',
    notes: d.notes || '',
    status: d.status || 'active',
    rate_hourly_min: d.rate_hourly_min ?? '',
    rate_hourly_max: d.rate_hourly_max ?? '',
    rate_monthly_min: d.rate_monthly_min ?? '',
    rate_monthly_max: d.rate_monthly_max ?? '',
    rate_hours_per_day: d.rate_hours_per_day ?? '',
    rate_days_per_week: d.rate_days_per_week ?? '',
    overtime_hourly_rate: d.overtime_hourly_rate ?? '',
    rate_currency: d.rate_currency || 'USD',
    rate_notes: d.rate_notes || '',
  };
}

export default function HelpDeskJobSupportProvider() {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contactQuery, setContactQuery] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyRateForm });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailForm, setDetailForm] = useState({ ...emptyRateForm });
  const [detailContact, setDetailContact] = useState(null);
  const [detailCompany, setDetailCompany] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [seekerDrawerId, setSeekerDrawerId] = useState(null);
  const [seekerDrawerOpen, setSeekerDrawerOpen] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [termsVersion, setTermsVersion] = useState('');
  const [providersForAssign, setProvidersForAssign] = useState([]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/helpdesk/job-support/providers', { params: { scope: 'all' } });
      setRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
    api
      .get('/helpdesk/job-support/workflow-meta')
      .then((r) => {
        setTermsText(r.data?.data?.payment_terms_text || '');
        setTermsVersion(r.data?.data?.payment_terms_version || '');
      })
      .catch(() => {});
    api
      .get('/helpdesk/job-support/providers', { params: { status: 'active' } })
      .then((r) => setProvidersForAssign(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setProvidersForAssign([]));
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get('/helpdesk/job-support/eligible-contacts', { params: { q: contactQuery || undefined, limit: 100 } })
        .then((r) => {
          if (!cancelled) setContacts(Array.isArray(r.data?.data) ? r.data.data : []);
        })
        .catch(() => {
          if (!cancelled) setContacts([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [contactQuery]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get('/accounts', {
          params: { limit: 100, search: companyQuery || undefined, status: 'active' },
          skipErrorToast: true,
        })
        .then((r) => {
          if (cancelled) return;
          const data = r.data?.data;
          const list = Array.isArray(data) ? data : data?.accounts || data?.rows || [];
          setCompanies(list);
        })
        .catch(() => {
          if (!cancelled) setCompanies([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [companyQuery]);

  const openProvider = async (id) => {
    setDrawerOpen(true);
    setLoadingDetail(true);
    setError('');
    try {
      const r = await api.get(`/helpdesk/job-support/providers/${id}`);
      const d = r.data?.data || null;
      setDetail(d);
      if (d) {
        setDetailForm(formFromDetail(d));
        setDetailContact(d.contact || null);
        setDetailCompany(d.company || null);
      }
    } catch (e) {
      setDetail(null);
      setError(e.response?.data?.message || 'Failed to load provider');
    } finally {
      setLoadingDetail(false);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedContact?.id) {
      setError('Select a person who can provide Job Support.');
      return;
    }
    if (form.provider_kind === 'company' && !selectedCompany?.id) {
      setError('For a company provider, select a company from Accounts.');
      return;
    }
    if (!form.skills?.trim()) {
      setError('List the skills this provider can offer.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/helpdesk/job-support/providers', {
        contact_id: selectedContact.id,
        provider_kind: form.provider_kind,
        account_id: form.provider_kind === 'company' ? selectedCompany.id : null,
        skills: form.skills,
        notes: form.notes,
        status: form.status,
        ...toPayloadRates(form),
      });
      const id = r.data?.data?.id;
      await loadList();
      setTab(0);
      if (id) openProvider(id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create provider');
    } finally {
      setSaving(false);
    }
  };

  const saveDetail = async () => {
    if (!detail?.id) return;
    setSaving(true);
    setError('');
    try {
      const r = await api.patch(`/helpdesk/job-support/providers/${detail.id}`, {
        contact_id: detailContact?.id,
        provider_kind: detailForm.provider_kind,
        account_id: detailForm.provider_kind === 'company' ? detailCompany?.id : null,
        skills: detailForm.skills,
        notes: detailForm.notes,
        status: detailForm.status,
        ...toPayloadRates(detailForm),
      });
      setDetail(r.data?.data || null);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const openSeeker = (requestId) => {
    setSeekerDrawerId(requestId);
    setSeekerDrawerOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Job Support Provider
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        View/List providers with rate ranges and who they support. Open a row to edit rates and review assigned
        seekers (extensions and monthly payment approvals).
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="View / List" />
        <Tab label="Add Job Support Provider" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TabPanel value={tab} index={0}>
        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider #</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Hourly $</TableCell>
                    <TableCell>Monthly $</TableCell>
                    <TableCell>Supporting (seekers)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingList ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>No providers yet. Add one from the next tab.</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openProvider(r.id)}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.provider_number}</TableCell>
                        <TableCell>{contactLabel(r.contact)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.provider_kind === 'company' ? 'Company' : 'Individual'}
                            color={r.provider_kind === 'company' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatRateRange(r.rate_hourly_min, r.rate_hourly_max, '/hr')}</TableCell>
                        <TableCell>
                          {formatRateRange(r.rate_monthly_min, r.rate_monthly_max, '/mo')}
                          {r.rate_hours_per_day || r.rate_days_per_week
                            ? ` · ${r.rate_hours_per_day || '—'}h/d × ${r.rate_days_per_week || '—'}d/w`
                            : ''}
                        </TableCell>
                        <TableCell
                          sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {supportingSummary(r.seekerRequests)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.status}
                            color={r.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button size="small" onClick={() => openProvider(r.id)}>
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Paper sx={{ p: 2, maxWidth: 900 }} component="form" onSubmit={submitNew}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Add Job Support Provider
          </Typography>
          <Autocomplete
            options={contacts}
            getOptionLabel={contactLabel}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            value={selectedContact}
            onChange={(_, v) => setSelectedContact(v)}
            onInputChange={(_, v) => setContactQuery(v)}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Provider person"
                required
                margin="normal"
                helperText="Candidate, Employee, or Contractor who can provide support"
              />
            )}
          />

          <FormControl sx={{ mt: 1, mb: 1 }}>
            <FormLabel>Provider type</FormLabel>
            <RadioGroup
              row
              value={form.provider_kind}
              onChange={(e) => {
                const kind = e.target.value;
                setForm((p) => ({ ...p, provider_kind: kind }));
                if (kind === 'individual') setSelectedCompany(null);
              }}
            >
              <FormControlLabel value="individual" control={<Radio />} label="Individual" />
              <FormControlLabel value="company" control={<Radio />} label="Company" />
            </RadioGroup>
          </FormControl>

          {form.provider_kind === 'company' && (
            <Autocomplete
              options={companies}
              getOptionLabel={(a) =>
                a ? `${a.company_name || a.name || ''}${a.account_code ? ` (${a.account_code})` : ''}` : ''
              }
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedCompany}
              onChange={(_, v) => setSelectedCompany(v)}
              onInputChange={(_, v) => setCompanyQuery(v)}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField {...params} label="Company (from Accounts)" required margin="normal" />
              )}
            />
          )}

          <TextField
            label="Skills they can provide"
            fullWidth
            required
            multiline
            minRows={3}
            margin="normal"
            value={form.skills}
            onChange={(e) => setForm((p) => ({ ...p, skills: e.target.value }))}
          />
          <RateFields form={form} onChange={setForm} />
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
          <TextField
            select
            label="Status"
            fullWidth
            margin="normal"
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
            sx={{ maxWidth: 240 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Add Job Support Provider'}
            </Button>
            <Button onClick={() => setTab(0)}>Back to list</Button>
          </Stack>
        </Paper>
      </TabPanel>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !saving && setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}
      >
        {loadingDetail ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !detail ? (
          <Alert severity="info">Select a provider from the list.</Alert>
        ) : (
          <>
            <Typography variant="h6" fontWeight={700}>
              {detail.provider_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {contactLabel(detail.contact)}
            </Typography>

            <Autocomplete
              options={contacts}
              getOptionLabel={contactLabel}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={detailContact}
              onChange={(_, v) => setDetailContact(v)}
              onInputChange={(_, v) => setContactQuery(v)}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="Provider person" margin="normal" />}
            />

            <FormControl sx={{ mt: 1, mb: 1 }}>
              <FormLabel>Provider type</FormLabel>
              <RadioGroup
                row
                value={detailForm.provider_kind}
                onChange={(e) => {
                  const kind = e.target.value;
                  setDetailForm((p) => ({ ...p, provider_kind: kind }));
                  if (kind === 'individual') setDetailCompany(null);
                }}
              >
                <FormControlLabel value="individual" control={<Radio />} label="Individual" />
                <FormControlLabel value="company" control={<Radio />} label="Company" />
              </RadioGroup>
            </FormControl>

            {detailForm.provider_kind === 'company' && (
              <Autocomplete
                options={companies}
                getOptionLabel={(a) =>
                  a ? `${a.company_name || a.name || ''}${a.account_code ? ` (${a.account_code})` : ''}` : ''
                }
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                value={detailCompany}
                onChange={(_, v) => setDetailCompany(v)}
                onInputChange={(_, v) => setCompanyQuery(v)}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label="Company (from Accounts)" margin="normal" />
                )}
              />
            )}

            <TextField
              label="Skills"
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              value={detailForm.skills}
              onChange={(e) => setDetailForm((p) => ({ ...p, skills: e.target.value }))}
            />
            <RateFields form={detailForm} onChange={setDetailForm} />
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              margin="normal"
              value={detailForm.notes}
              onChange={(e) => setDetailForm((p) => ({ ...p, notes: e.target.value }))}
            />
            <TextField
              select
              label="Status"
              fullWidth
              margin="normal"
              value={detailForm.status}
              onChange={(e) => setDetailForm((p) => ({ ...p, status: e.target.value }))}
              sx={{ maxWidth: 240 }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>

            <Button variant="contained" disabled={saving} onClick={saveDetail} sx={{ mt: 1 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Providing support to
            </Typography>
            {(detail.seekerRequests || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No seekers assigned yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {(detail.seekerRequests || []).map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {s.request_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {contactLabel(s.contact)} · Step {s.workflow_step || '—'} ·{' '}
                          {(s.expected_start_date || '—') + ' → ' + (s.expected_end_date || '—')}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => openSeeker(s.id)}>
                        Open workflow
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            <Button fullWidth sx={{ mt: 3 }} onClick={() => setDrawerOpen(false)} disabled={saving}>
              Close
            </Button>
          </>
        )}
      </Drawer>

      <JobSupportSeekerWorkflowDrawer
        requestId={seekerDrawerId}
        open={seekerDrawerOpen}
        onClose={() => setSeekerDrawerOpen(false)}
        onUpdated={() => {
          loadList();
          if (detail?.id) openProvider(detail.id);
        }}
        providers={providersForAssign}
        termsText={termsText}
        termsVersion={termsVersion}
      />
    </Box>
  );
}
